import React, { useState, useEffect, useMemo } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from "react-table";
import { useAuth } from "../context/AuthContext";
import {
  FaTable,
  FaSearch,
  FaPrint,
  FaCalendarAlt,
  FaShareAlt,
  FaFileExcel,
  FaFileCsv,
  FaFilePdf,
  FaTimesCircle,
} from "react-icons/fa";

import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function Sales() {
  const [sales, setSales] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { loginType: rawLoginType, branch } = useAuth();
  const loginType = Number(rawLoginType || 0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("My Restaurant");
   const [data, setData] = useState([]);

  // modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelRemark, setCancelRemark] = useState("");
  const [modalType, setModalType] = useState("");

  const [branchDetails, setBranchDetails] = useState(null); // store branch details

  const openCancelModal = (transaction_number) => {
    setSelectedTxn(transaction_number);
    setCancelMessage("");
    setCancelRemark("");
    setModalType("cancel");
    setShowModal(true);
  };

  // Fetch Branch Details
  useEffect(() => {
    fetch(
      `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=branches&condition=get_branch_det&branch=${branch}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setBranchDetails(data[0]);
        }
      })
      .catch((err) => console.error("Error fetching branch details:", err));
  }, [branch]);

  // GST condition check
  const gstEnabled = branchDetails?.gst_bill === "Yes";

  const confirmCancel = async () => {
    if (!selectedTxn) return;
    if (!cancelRemark.trim()) {
      setCancelMessage("Remark is required to cancel the bill.");
      return;
    }
    setCancelLoading(true);
    setCancelMessage("");
    try {
      const cancelUrl = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=set_cancel_bill&transaction_number=${selectedTxn}&remark=${encodeURIComponent(
        cancelRemark
      )}`;
      const res = await fetch(cancelUrl);
      const result = await res.json();
      if (result.status === "ok") {
        setCancelMessage("Bill cancelled successfully");
        fetchSales(fromDate, toDate);
        setShowModal(false);
      } else {
        setCancelMessage("Failed to cancel bill");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      setCancelMessage("Error cancelling bill");
    }
    setCancelLoading(false);
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await fetch(
          `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=company&transac=get`
        );
        const result = await response.json();
        if (result.status === "ok" && result.data.length > 0) {
          setCompanyName(result.data[0].company_name);
        }
      } catch (err) {
        console.error("Error fetching company:", err);
      }
    };
    fetchCompany();
  }, []);

  const formatDateYMD = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const formatDateDMY = (date) => {
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const fetchSales = async (from = fromDate, to = toDate) => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const url = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get_billing_details_by_dates_all&from_date=${from}&to_date=${to}&condition=branch&branch=${branch}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error("Invalid data received from API", data);
        setSales([]);
        setLoading(false);
        return;
      }

      const formattedData = data.map((item, index) => ({
        sno: index + 1,
        invoice_date: item.invoice_date ? formatDateDMY(item.invoice_date) : "",
        invoice_time: item.invoice_time || "",
        transaction_number: item.transaction_number || "",
        name: item.customer_name || "",
        mobile: item.customer_mobile || "",
        table: item.table_name || "",
        updated_by_name: item.updated_by_name || "",
        taxable: parseFloat(item.invoice_taxable) || 0,
        tax: parseFloat(item.invoice_tax_amt) || 0,
        subtotal: parseFloat(item.subtotal) || 0,
        discount: parseFloat(item.invoice_disc_amt) || 0,
        voucher: parseFloat(item.coupon_discount) || 0,
        additional: parseFloat(item.invoice_additional_charges) || 0,
        roundoff: parseFloat(item.adjust_amount) || 0,
        invoice: parseFloat(item.invoice_value) || 0,
        payment_mode: item.payment_mode || "",
        cancel_status: Number(item.cancel_status) || 0,
        remark: item.remark || "",
      }));

      setSales(formattedData);
	  setData(formattedData);
    } catch (error) {
      console.error("Error fetching sales data:", error);
      setSales([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const today = formatDateYMD(new Date());
    setFromDate(today);
    setToDate(today);
    fetchSales(today, today);
  }, []);

  const filteredSales = useMemo(
    () =>
      sales.filter(
        (sale) =>
          sale.name.toLowerCase().includes(searchText.toLowerCase()) ||
          sale.mobile.includes(searchText) ||
          sale.table.toLowerCase().includes(searchText.toLowerCase())
      ),
    [sales, searchText]
  );

  const totals = useMemo(() => {
    return filteredSales.reduce(
      (acc, curr) => {
        const target = curr.cancel_status === 1 ? acc.cancel : acc.active;
        target.subtotal += curr.subtotal;
        target.discount += curr.discount;
        target.voucher += curr.voucher;
        target.additional += curr.additional;
        target.roundoff += curr.roundoff;
        target.invoice += curr.invoice;
        target.taxable += curr.taxable;
        target.tax += curr.tax;
        return acc;
      },
      {
        active: {
          subtotal: 0,
          discount: 0,
          voucher: 0,
          additional: 0,
          roundoff: 0,
          invoice: 0,
          taxable: 0,
          tax: 0,
        },
        cancel: {
          subtotal: 0,
          discount: 0,
          voucher: 0,
          additional: 0,
          roundoff: 0,
          invoice: 0,
          taxable: 0,
          tax: 0,
        },
      }
    );
  }, [filteredSales]);

  const columns = useMemo(() => {
    const baseColumns = [
      { Header: "S.No", accessor: "sno" },
      {
        Header: "Date & Time",
        accessor: "invoice_date",
        Cell: ({ row }) => (
          <div>
            <div>{row.original.invoice_date}</div>
            <div className="text-xs text-gray-300">{row.original.invoice_time}</div>
          </div>
        ),
      },
      {
        Header: "Customer",
        accessor: "name",
        Cell: ({ row }) => (
          <div>
            <div>{row.original.name}</div>
            <div className="text-xs text-gray-300">{row.original.transaction_number}</div>
          </div>
        ),
      },
      { Header: "Mobile", accessor: "mobile" },
      {
        Header: "Table",
        accessor: "table",
        Cell: ({ row }) => (
          <div>
            <div>{row.original.table}</div>
            <div className="text-xs text-gray-300">By: {row.original.updated_by_name}</div>
          </div>
        ),
      },
    ];

    if (gstEnabled) {
      baseColumns.push(
        { Header: "Taxable Value", accessor: "taxable" },
        { Header: "Tax Amount", accessor: "tax" }
      );
    }

    baseColumns.push(
      { Header: "Subtotal", accessor: "subtotal" },
      { Header: "Discount", accessor: "discount" },
      { Header: "Voucher", accessor: "voucher" },
      { Header: "Additional Charges", accessor: "additional" },
      { Header: "Roundoff", accessor: "roundoff" },
      { Header: "Invoice Value", accessor: "invoice" },
      { Header: "Payment Mode", accessor: "payment_mode" },
      {
        Header: "Remark",
        accessor: "remark",
        Cell: ({ row }) =>
          row.original.cancel_status === 1
            ? `Bill Cancelled - ${row.original.remark}`
            : "",
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="flex gap-1 flex-wrap text-sm">
            <button
              onClick={() =>
                window.open(`${window.location.origin}/bill-print/${row.original.transaction_number}`, "_blank")
              }
              className="bg-gray-800 text-white px-2 py-1 rounded flex items-center gap-1 text-sm"
            >
              <FaPrint /> Print
            </button>

            <button
              onClick={() => {
                const shareUrl = `${window.location.origin}/bill-print/${row.original.transaction_number}`;
                if (row.original.mobile) {
                  const waUrl = `https://wa.me/${row.original.mobile}?text=Here%20is%20your%20bill:%20${encodeURIComponent(
                    shareUrl
                  )}`;
                  window.open(waUrl, "_blank");
                } else {
                  navigator.clipboard.writeText(shareUrl);
                  alert("Bill link copied to clipboard!");
                }
              }}
              className="bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-1 text-sm"
            >
              <FaShareAlt /> Share
            </button>

            {(loginType === 1 || loginType === 2) && row.original.cancel_status !== 1 && (
              <button
                onClick={() => openCancelModal(row.original.transaction_number)}
                className="bg-red-600 text-white px-2 py-1 rounded flex items-center gap-1 text-sm"
              >
                <FaTimesCircle /> Cancel
              </button>
            )}
          </div>
        ),
      }
    );

    return baseColumns;
  }, [gstEnabled, loginType]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    setGlobalFilter,
  } = useTable(
    { columns, data: filteredSales, initialState: { pageIndex: 0, pageSize: 5 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter(searchText || undefined);
  }, [searchText, setGlobalFilter]);

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredSales);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales");
    XLSX.writeFile(wb, "Sales_Report.xlsx");
  };

  const exportCSV = () => {
    const ws = XLSX.utils.json_to_sheet(filteredSales);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "Sales_Report.csv");
  };


  
  
  const exportPDF = () => {
  const doc = new jsPDF();
  doc.text("Sales Table", 20, 10);

  autoTable(doc, {
    head: [columns.filter((c) => c.accessor).map((c) => c.Header)],
    body: data.map((row) => columns.filter((c) => c.accessor).map((c) => row[c.accessor])),
    startY: 20,
  });

  doc.save("Sales_Report.pdf");
};

  return (
    <Layout>
      <div className="p-4 bg-gradient-to-b from-blue-900 to-black min-h-screen text-white text-sm">
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded shadow-lg text-black">
              Fetching data...
            </div>
          </div>
        )}

        <h2 className="text-xl font-bold mb-3 flex items-center">
          <FaTable className="mr-2" /> Sales Report
        </h2>

        {/* Filters */}
        <div className="flex flex-wrap mb-3 items-center gap-2 text-sm">
          <div className="flex items-center gap-1">
            <FaCalendarAlt />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border rounded p-1 text-black text-sm"
            />
          </div>

          <div className="flex items-center gap-1">
            <FaCalendarAlt />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border rounded p-1 text-black text-sm"
            />
          </div>

          <button
            onClick={() => fetchSales(fromDate, toDate)}
            className="bg-green-600 text-white px-3 py-1 rounded text-sm"
          >
            Fetch Data
          </button>

          <div className="flex items-center gap-1">
            <FaSearch />
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="border rounded p-1 text-black text-sm"
            />
          </div>

          {/* Export Buttons */}
          <button
            onClick={exportExcel}
            className="bg-green-700 text-white px-3 py-1 rounded flex items-center gap-1"
          >
            <FaFileExcel /> Excel
          </button>
          <button
            onClick={exportCSV}
            className="bg-yellow-600 text-white px-3 py-1 rounded flex items-center gap-1"
          >
            <FaFileCsv /> CSV
          </button>
          <button
            onClick={exportPDF}
            className="bg-red-600 text-white px-3 py-1 rounded flex items-center gap-1"
          >
            <FaFilePdf /> PDF
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto text-sm">
          <table
            {...getTableProps()}
            className="min-w-full border border-gray-700 text-sm"
          >
            <thead className="bg-blue-800 text-sm">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="border px-2 py-1 text-left text-white text-sm"
                    >
                      {column.render("Header")}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? " 🔽"
                            : " 🔼"
                          : ""}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="text-sm">
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className={`border px-2 py-1 text-sm ${
                          Number(row.original.cancel_status) === 1
                            ? "bg-red-900 text-white font-semibold"
                            : "even:bg-blue-950 hover:bg-blue-900"
                        }`}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between mt-2 text-sm">
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="px-3 py-1 border rounded bg-blue-700 text-sm"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {pageIndex + 1} of {pageOptions.length}
          </span>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="px-3 py-1 border rounded bg-blue-700 text-sm"
          >
            Next
          </button>
        </div>

 {/* Totals */}
        <div className="mt-3 p-2 border rounded bg-blue-950 text-white text-sm">
          <h3 className="font-bold mb-2 text-sm">Totals:</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded bg-green-800">
              <h4 className="font-semibold text-green-200">Active Bills</h4>
             {gstEnabled && <p>Taxable Value: ₹{totals.active.taxable.toFixed(2)}</p>}
             {gstEnabled &&  <p>Tax Value: ₹{totals.active.tax.toFixed(2)}</p>}
              <p>Subtotal: ₹{totals.active.subtotal.toFixed(2)}</p>
              <p>Discount: ₹{totals.active.discount.toFixed(2)}</p>
              <p>Voucher: ₹{totals.active.voucher.toFixed(2)}</p>
              <p>Additional Charges: ₹{totals.active.additional.toFixed(2)}</p>
              <p>Roundoff: ₹{totals.active.roundoff.toFixed(2)}</p>
              <p>Invoice Value: ₹{totals.active.invoice.toFixed(2)}</p>
            </div>
            <div className="p-3 rounded bg-red-800">
              <h4 className="font-semibold text-red-200">Cancelled Bills</h4>
               {gstEnabled &&  <p>Taxable Value: ₹{totals.cancel.taxable.toFixed(2)}</p>}
               {gstEnabled &&  <p>Tax Value: ₹{totals.cancel.tax.toFixed(2)}</p>}
              <p>Subtotal: ₹{totals.cancel.subtotal.toFixed(2)}</p>
              <p>Discount: ₹{totals.cancel.discount.toFixed(2)}</p>
              <p>Voucher: ₹{totals.cancel.voucher.toFixed(2)}</p>
              <p>Additional Charges: ₹{totals.cancel.additional.toFixed(2)}</p>
              <p>Roundoff: ₹{totals.cancel.roundoff.toFixed(2)}</p>
              <p>Invoice Value: ₹{totals.cancel.invoice.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Cancel Modal */}
        {showModal && modalType === "cancel" && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded shadow-lg w-96">
              <h2 className="text-lg font-bold mb-2">Cancel Bill</h2>
              <p>Transaction No: {selectedTxn}</p>
              <textarea
                placeholder="Enter remark"
                value={cancelRemark}
                onChange={(e) => setCancelRemark(e.target.value)}
                className="border rounded w-full p-2 mt-2 text-black"
              />
              {cancelMessage && (
                <p className="text-red-600 mt-1">{cancelMessage}</p>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-3 py-1 rounded border"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCancel}
                  disabled={cancelLoading}
                  className="px-3 py-1 rounded bg-red-600 text-white"
                >
                  {cancelLoading ? "Cancelling..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Sales;
